<template>
  <div>
    <!-- Grupo compartido por todos los Modulos y por el bloque de sueltas: una
         actividad se arrastra para reordenarla o para moverla a otro Modulo. -->
    <draggable
      v-model="draggableList"
      :disabled="!allows_crud"
      :group="{ name: 'instructional-activities' }"
      handle=".handle"
      tag="div"
      class="kl-activity-drop"
      :class="{ 'kl-activity-drop-empty': allows_crud && activities.length === 0 }"
    >
      <ActivityCard
        v-for="activity in draggableList"
        :key="activity.id"
        :activity="activity"
        :instructional_design_id="instructional_design_id"
        :study_units="study_units"
        :bibliographic_resources="bibliographic_resources"
        :teaching_support_resources="teaching_support_resources"
        :activity_weeks="activity_weeks"
        :time_allocation_rows="time_allocation_rows"
        :evaluation_situations="evaluation_situations"
        :evaluation_list="evaluation_list"
        :highlight_activity_id="highlight_activity_id"
        :level_key="level_key"
        :allows_crud="allows_crud"
        :shows_ai="shows_ai"
        :egress_profile_matter_id="egress_profile_matter_id"
        @resources-changed="$emit('resources-changed')"
      ></ActivityCard>
    </draggable>

    <p v-if="activities.length === 0" class="kl-activity-empty">
      {{ emptyText }}
    </p>
  </div>
</template>

<script>
import draggable from "vuedraggable";

export default {
  name: "ActivityList",
  components: {
    draggable,
    ActivityCard: () => import("./ActivityCard"),
  },
  props: {
    activities: {
      type: Array,
      default: () => [],
    },
    instructional_design_id: {
      type: Number,
      required: true,
    },
    // Modulo que aloja esta lista; null => actividades sueltas. Es el
    // destino que se le asigna a lo que se suelte acá.
    module_id: {
      type: Number,
      default: null,
    },
    study_units: {
      type: Array,
      default: () => [],
    },
    bibliographic_resources: {
      type: Array,
      default: () => [],
    },
    teaching_support_resources: {
      type: Array,
      default: () => [],
    },
    // Semana calculada de cada actividad: Map(activity_id -> {from,to,overflow}).
    activity_weeks: {
      type: Map,
      default: () => new Map(),
    },
    // E38 · Lo que el formulario del Programa pide como props: las horas por Ambiente de
    // Aprendizaje y el catálogo de situaciones evaluativas. Viajan desde el orquestador.
    time_allocation_rows: {
      type: Array,
      default: () => [],
    },
    evaluation_situations: {
      type: Array,
      default: () => [],
    },
    // Todas las piezas de la asignatura: el formulario las usa para el tope del 100%.
    evaluation_list: {
      type: Array,
      default: () => [],
    },
    highlight_activity_id: {
      type: Number,
      default: null,
    },
    // E37 · Qué wrapper es la pieza: "matter" o "egress_profile_matter". Decide a qué endpoint
    // va el PATCH del arrastre. Lo resuelve el orquestador con `use_matter_values`.
    level_key: {
      type: String,
      default: "egress_profile_matter",
    },
    allows_crud: {
      type: Boolean,
      default: false,
    },
    // E45 · El gating de la IA y la Asignatura del Perfil de Egreso que necesita el
    // builder. Sólo pasan por acá para llegar a ActivityCard, que es donde vive el botón
    // de regenerar.
    shows_ai: {
      type: Boolean,
      default: false,
    },
    egress_profile_matter_id: {
      type: Number,
      default: null,
    },
    empty_text: {
      type: String,
      default: "",
    },
  },
  computed: {
    piecesEntityNamePlural() {
      return `${this.$getVisibleNames(
        "manual.instructional_design.activity",
        true,
        "Actividades"
      )}/${this.$getVisibleNames(
        "evaluations2.evaluation",
        true,
        "Evaluaciones"
      )}`;
    },
    emptyText() {
      return (
        this.empty_text ||
        this.$t("teaching.instructional.activityListEmpty", {
          entity: this.piecesEntityNamePlural,
        })
      );
    },
    // E30 · Antes esto vivía en el componente de momento y el arrastre movía la actividad ENTRE
    // MOMENTOS. Los momentos dejaron de ser cajas: el arrastre ahora sólo reordena
    // dentro del Modulo o mueve a otro Modulo.
    draggableList: {
      // El getter NO renumera. Antes normalizaba `order` a 1..N escribiendo en los
      // objetos del store sin persistirlo: al guardar la actividad, el PATCH devolvía
      // su `order` real y quedaba desalineada, saltando de posición y cambiándole la
      // semana (§2.8). Desde E30 el `order` es una sola secuencia por Modulo, así
      // que ya no hace falta desempatar por id.
      get() {
        return [...this.activities].sort((a, b) => a.order - b.order || a.id - b.id);
      },
      // El setter ya NO persiste: sólo deja el arreglo en el orden nuevo y avisa hacia arriba.
      //
      // E37 · El `order` pasó a ser una secuencia ÚNICA de todo el diseño, no una por
      // Modulo. Es el mismo campo que ordena la tabla del Programa, donde un 1 repetido en
      // cada Unidad no significaría nada. Esa numeración sólo se puede calcular arriba, que es
      // donde se ven todos los Modulos a la vez: acá se escribe una posición provisoria
      // que le dice al orquestador en qué lugar de ESTA lista quedó cada pieza.
      set(list) {
        list.forEach((element, index) => {
          element.order = index + 1;
          element.module = this.module_id;
        });
        this.$emit("reordered");
      },
    },
  },
};
</script>

<style scoped>
/* Editando, la lista vacía tiene que seguir siendo un destino: sin alto no hay dónde
   soltar la actividad que viene de otro Modulo. */
.kl-activity-drop-empty {
  min-height: 2.4rem;
  border: 1px dashed var(--kl-table-border, #d9e0e0);
  border-radius: var(--kl-radius-md, 6px);
}
.kl-activity-empty {
  font-size: var(--kl-fs-small);
  color: #97a5a3;
  margin: 0.4rem 0 0;
}
</style>
